'use client'

import type { PropsWithChildren } from 'react'
import { createContext, useMemo } from 'react'

import { useAuth } from '~/hooks/useAuth'
import { useIsDemoMode } from '~/store/slices/demoMode'

import useBatteriesDataManager, { type BatteriesDataManagerContextValue } from './batteries'
import useFuelTanksDataManager, { type FuelTanksDataManagerContextValue } from './fuelTanks'
import useGeneratorsDataManager, { type GeneratorsDataManagerContextValue } from './generators'
import useGridConnectionsDataManager, { type GridConnectionsDataManagerContextValue } from './gridConnections'
import useLoadsDataManager, { type LoadsDataManagerContextValue } from './loads'
import useOrgTreeUsersDataManager, { type OrgTreeUsersDataManagerContextValue } from './orgTreeUsers'
import useCustomersDataManager, { type CustomersDataManagerContextValue } from './organizations'
import useSiteDataManager, { type SitesDataManagerContextValue } from './sites'
import useSolarArraysDataManager, { type SolarArraysDataManagerContextValue } from './solarArrays'
import { IndexedDbService, type StoreDefinition } from './storage/IndexedDbService'
import useSynopLinksDataManager, { type SynopLinksDataManagerContextValue } from './synoplinks'
import useIndexedDbCleanup from './useIndexedDbCleanup'
import useUsersDataManager, { type UsersDataManagerContextValue } from './users'
import useVehicleDataManager, { type VehiclesDataManagerContextValue } from './vehicles'

type DataManagementContextValue = {
  vehicles: VehiclesDataManagerContextValue
  sites: SitesDataManagerContextValue
  customers: CustomersDataManagerContextValue
  users: UsersDataManagerContextValue
  orgTreeUsers: OrgTreeUsersDataManagerContextValue
  synoplinks: SynopLinksDataManagerContextValue
  batteries: BatteriesDataManagerContextValue
  generators: GeneratorsDataManagerContextValue
  solarArrays: SolarArraysDataManagerContextValue
  fuelTanks: FuelTanksDataManagerContextValue
  gridConnections: GridConnectionsDataManagerContextValue
  loads: LoadsDataManagerContextValue
}

export const DataManagementContext = createContext<DataManagementContextValue | undefined>(undefined)

const SCHEMA_VERSION = 10
const STORE_DEFINITIONS: StoreDefinition[] = [
  { name: 'vehicles', options: { keyPath: 'id' } },
  { name: 'sites', options: { keyPath: 'id' } },
  { name: 'customers', options: { keyPath: 'id' } },
  { name: 'users', options: { keyPath: 'id' } },
  { name: 'orgTreeUsers', options: { keyPath: 'id' } },
  { name: 'synoplinks', options: { keyPath: 'id' } },
  { name: 'batteries', options: { keyPath: 'id' } },
  { name: 'generators', options: { keyPath: 'id' } },
  { name: 'solarArrays', options: { keyPath: 'id' } },
  { name: 'fuelTanks', options: { keyPath: 'id' } },
  { name: 'gridConnections', options: { keyPath: 'id' } },
  { name: 'loads', options: { keyPath: 'id' } }
]

export const DB_PREFIX = 'synop_cache_'
export const DB_OPTIONS = {
  version: SCHEMA_VERSION,
  stores: STORE_DEFINITIONS
}

/**
 * Demo mode (see `src/features/demoMode/`) uses a separate cache database so real
 * customer data cached before a demo can never hydrate into an anonymized session,
 * and anonymized snapshots never pollute the real cache.
 */
export function getCacheDbName(userId: string | undefined, isDemoMode: boolean) {
  return `${DB_PREFIX}${userId}${isDemoMode ? '_demo' : ''}`
}

function useIdbService(userId: string | undefined, isDemoMode: boolean) {
  return useMemo(() => {
    return new IndexedDbService({ ...DB_OPTIONS, name: getCacheDbName(userId, isDemoMode) })
  }, [userId, isDemoMode])
}

export function DataManagementProvider({ children }: PropsWithChildren) {
  const { user } = useAuth()
  const isDemoMode = useIsDemoMode()
  useIndexedDbCleanup({ isUnauthenticated: !user, userId: user?.id, isDemoMode })
  const service = useIdbService(user?.id, isDemoMode)

  const vehicles = useVehicleDataManager(service)
  const sites = useSiteDataManager(service)
  const customers = useCustomersDataManager(service)
  const users = useUsersDataManager(service)
  const orgTreeUsers = useOrgTreeUsersDataManager(service)
  const synoplinks = useSynopLinksDataManager(service)
  const batteries = useBatteriesDataManager(service)
  const generators = useGeneratorsDataManager(service)
  const solarArrays = useSolarArraysDataManager(service)
  const fuelTanks = useFuelTanksDataManager(service)
  const gridConnections = useGridConnectionsDataManager(service)
  const loads = useLoadsDataManager(service)
  const value = useMemo<DataManagementContextValue>(
    () => ({
      vehicles,
      sites,
      customers,
      users,
      orgTreeUsers,
      synoplinks,
      batteries,
      generators,
      solarArrays,
      fuelTanks,
      gridConnections,
      loads
    }),
    [
      vehicles,
      sites,
      customers,
      users,
      orgTreeUsers,
      synoplinks,
      batteries,
      generators,
      solarArrays,
      fuelTanks,
      gridConnections,
      loads
    ]
  )

  return <DataManagementContext.Provider value={value}>{children}</DataManagementContext.Provider>
}
